import { Suspense, useRef, lazy, useEffect, useMemo, useCallback } from 'react';
import { BoxHelper, Euler, Matrix4 } from 'three';
import { useHelper } from "@react-three/drei";
import { debug } from "../utils/logger";
import { useShadow } from "../hooks/useShadow";
import { useCustomGltf } from "../hooks/useCustomGLTF";
import { useWireframe } from "../hooks/useWireframe";
import { setGltfScalePosition } from '../utils/setGltfScalePosition';
import { printGltfSceneStructure } from "../utils/gltfInfo";
import { DEG2RAD } from 'three/src/math/MathUtils';

const ModelDebugStatsView = lazy(() => import("../components/ModelDebugStatsView"));

const SuspendedModel = ({
	                        setModel,
	                        url,
	                        rotate,
	                        showHelper,
	                        onLoadProgress,
	                        showWireframe,
	                        modelContent,
	                        dontScale,
	                        debugPrintStructure,
	                        showModelStats,
	                        debugPrintMeshDetailsOnClick,
	                        onClick,
	                        onPointerOver,
	                        onPointerOut,
                        }) => {
	const ref = useRef(null);
	const gltf = useCustomGltf(url, onLoadProgress);

	useShadow(gltf.scene);
	// Set default pose if there are any skinned meshes with animations
	// We want the vertices to be in final position before
	// we calculate scaling and positioning
	gltf.scene.traverse((o) => {
		if (o.isSkinnedMesh) {
			o.pose();
		}
	});
	useWireframe(gltf.scene, showWireframe);
	useHelper(showHelper ? ref : null, BoxHelper, "black");

	useMemo(() => {
		if (debugPrintStructure) {
			printGltfSceneStructure(gltf)
		}
	}, [debugPrintStructure, gltf]);

	useEffect(() => {
		const eulerRotateXRad = rotate.x * DEG2RAD;
		const eulerRotateYRad = rotate.y * DEG2RAD;
		const eulerRotateZRad = rotate.z * DEG2RAD;

		// Rotate needs to be before scale
		gltf.scene.rotation.set(eulerRotateXRad, eulerRotateYRad, eulerRotateZRad);
		gltf.scene.updateWorldMatrix(true, true);

		const initMat = new Matrix4().makeRotationFromEuler(new Euler(eulerRotateXRad, eulerRotateYRad, eulerRotateZRad));
		const boundsBox = setGltfScalePosition(gltf, dontScale, initMat);
		setModel(gltf, boundsBox);
	}, [gltf, url, dontScale, rotate.x, rotate.y, rotate.z]);

	useEffect(() => {
		if (gltf.scene) {
			// we want to fire a completed loading progress (1/1)
			// when a cached model is renderer
			onLoadProgress?.({ loaded: 1, total: 1 });
		}
	}, [gltf, onLoadProgress]);

	const onModelClick = useCallback( (e) => {
		if (debugPrintMeshDetailsOnClick) {
			//stop propagation or raycaster can find objects behind the one clicked and raise another event
			e.stopPropagation();
			if (e.object) {
				debug(`Object (name: ${e.object.name}) click identified!`, e.object);
			}
		}

		onClick?.(e);
	}, []);

	return (
		<>
			<primitive
				ref={ref}
				object={gltf.scene}
				castShadow
				receiveShadow
				onClick={debugPrintMeshDetailsOnClick ? onModelClick : undefined}
				onPointerOver={onPointerOver}
				onPointerOut={onPointerOut}
			>
				{modelContent}
			</primitive>
			<Suspense fallback={null}>
				{showModelStats && <ModelDebugStatsView gltf={gltf}/>}
			</Suspense>
		</>
	)
};

export const Model =
	({
		 onModelReady,
		 ...modelProps
	 }) => {

		//TODO: wrap the SuspendedModel with React.Suspense once we sort the ordering of things like shadows/scale/etc
		return (
				<SuspendedModel
					{...modelProps}
					setModel={onModelReady}
				/>
		);
	};
